import {
  type QueryClient,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import cloneDeep from 'lodash/cloneDeep';

import { getAllPreferences } from '../../../shared/user-preferences/api/user-preferences-api';
import {
  PAGE_ORDER_SCOPES,
  type PreferenceScope,
  UserPreferenceKey,
  type UserPreferences,
} from '../../../shared/user-preferences/types';
import {
  enqueuePageOrderSave,
  writePageOrderToCache,
} from '../../../shared/user-preferences/utils/page-order-queue';
import {
  createPage as apiCreatePage,
  deletePage as apiDeletePage,
  updatePage as apiUpdatePage,
} from '../api/build-a-dash-api';
import type { Page } from '../components/PageConfigProvider/types';
import { pageQueryKeys } from './usePagesQuery';

type AllPrefs = Record<PreferenceScope, Partial<UserPreferences>>;

/**
 * Returns cached prefs, or fetches them. Ensures cleanup runs even when the
 * delete happens before the preferences modal / tab-order hook have warmed
 * the cache in this session.
 */
async function loadAllPrefs(
  queryClient: QueryClient
): Promise<AllPrefs | undefined> {
  const cached = queryClient.getQueryData<AllPrefs>(['user-preferences']);
  if (cached) return cached;
  try {
    return await queryClient.fetchQuery<AllPrefs>({
      queryKey: ['user-preferences'],
      queryFn: getAllPreferences,
    });
  } catch (err) {
    console.error('Failed to load preferences for tab-order cleanup:', err);
    return undefined;
  }
}

async function cleanupDeletedPageFromOrder(
  queryClient: QueryClient,
  pageId: string
): Promise<void> {
  const allPrefs = await loadAllPrefs(queryClient);
  if (!allPrefs) return;

  // Mirrors `customTabId` in ./useTabOrder — kept inline here to avoid pulling
  // useTabOrder's module-level deps into this hook + its tests.
  const deletedTabId = `custom:${pageId}`;
  const dirtyScopes = PAGE_ORDER_SCOPES.filter((scope) => {
    const order = allPrefs[scope]?.[UserPreferenceKey.PageOrder] ?? [];
    return order.includes(deletedTabId);
  });
  if (dirtyScopes.length === 0) return;

  try {
    // Read each scope's latest cached order (which any in-flight reset / drag
    // save has already written to via writePageOrderToCache), then sync-write
    // the filtered result before enqueueing so subsequent writers see it too.
    await Promise.all(
      dirtyScopes.map((scope) => {
        const latest = queryClient.getQueryData<AllPrefs>(['user-preferences']);
        const order = latest?.[scope]?.[UserPreferenceKey.PageOrder] ?? [];
        const filtered = order.filter((tabId) => tabId !== deletedTabId);
        writePageOrderToCache(queryClient, scope, filtered);
        return enqueuePageOrderSave(scope, filtered);
      })
    );
  } catch (err) {
    // Stale tab IDs are filtered visually by mergeWithStoredOrder, so a failed
    // cleanup is non-fatal — surface it for observability and move on.
    console.error('Failed to clean up deleted page from tab order:', err);
  }

  await queryClient.invalidateQueries({ queryKey: ['user-preferences'] });
}

function optimisticUpdateHelper(
  queryClient: QueryClient,
  updater: (pages: Page[]) => Page[]
) {
  const prevPages = queryClient.getQueryData<Page[]>(pageQueryKeys.lists());
  if (prevPages) {
    queryClient.setQueryData<Page[]>(
      pageQueryKeys.lists(),
      updater(cloneDeep(prevPages))
    );
  }
  return prevPages;
}

export function usePageMutations() {
  const queryClient = useQueryClient();

  /** CREATE */
  const createPage = useMutation({
    mutationFn: apiCreatePage,
    onMutate: async (newPage: Page) => {
      await queryClient.cancelQueries({ queryKey: pageQueryKeys.lists() });
      const prevPages = optimisticUpdateHelper(queryClient, (pages) => [
        ...pages,
        newPage,
      ]);
      return { prevPages };
    },
    onError: (_err, _newPage, context) => {
      if (context?.prevPages) {
        queryClient.setQueryData(pageQueryKeys.lists(), context.prevPages);
      }
    },
  });

  /** UPDATE */
  const updatePage = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Page> }) =>
      apiUpdatePage(id, data),
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey: pageQueryKeys.lists() });
      const prevPages = optimisticUpdateHelper(queryClient, (pages) =>
        pages.map((p) =>
          p.id === variables.id ? ({ ...p, ...variables.data } as Page) : p
        )
      );
      return { prevPages };
    },
    onError: (_err, _variables, context) => {
      if (context?.prevPages) {
        queryClient.setQueryData(pageQueryKeys.lists(), context.prevPages);
      }
    },
  });

  /** DELETE */
  const deletePage = useMutation({
    mutationFn: (id: string) => apiDeletePage(id),
    onMutate: async (id: string) => {
      await queryClient.cancelQueries({ queryKey: pageQueryKeys.lists() });
      const prevPages = optimisticUpdateHelper(queryClient, (pages) =>
        pages.filter((p) => p.id !== id)
      );
      return { prevPages };
    },
    onError: (_err, _id, context) => {
      if (context?.prevPages) {
        queryClient.setQueryData(pageQueryKeys.lists(), context.prevPages);
      }
    },
    onSuccess: (_data, id) => cleanupDeletedPageFromOrder(queryClient, id),
  });

  return {
    handleCreatePage: createPage,
    handleUpdatePage: updatePage,
    handleDeletePage: deletePage,
  };
}
